import { useEffect, useRef, useState, useCallback, useMemo } from "react";

const TICK_INTERVAL_MS = 1000; // Check every second

export function useScheduleManager({
  schedules = [],
  serverTimeOffsetMs = 0,
  onScheduleActivate = null,
  onScheduleComplete = null,
}) {
  const [activeScheduleUrl, setActiveScheduleUrl] = useState(null);
  const [displayMode, setDisplayMode] = useState("default"); // "default" | "schedule"
  const tickRef = useRef(null);
  const lastActiveIdRef = useRef(null);
  const lastScheduleEndTimeRef = useRef(null); // Track when schedule should naturally end
  // Get server-adjusted current time
  const getNow = useCallback(() => Date.now() + serverTimeOffsetMs, [serverTimeOffsetMs]);
  // Validate and filter schedules
  const validSchedules = useMemo(() => {
    return schedules
      .filter((s) => {
        if (!s?.scheduleId || !s?.startAt || !s?.url) return false;
        const start = new Date(s.startAt).getTime();
        const end = s.endAt ? new Date(s.endAt).getTime() : Infinity;
        return !isNaN(start) && !isNaN(end) && end > start;
      })
      .sort((a, b) => {
        const aStart = new Date(a.startAt).getTime();
        const bStart = new Date(b.startAt).getTime();
        if (bStart !== aStart) return bStart - aStart;
        const aEnd = a.endAt ? new Date(a.endAt).getTime() : Infinity;
        const bEnd = b.endAt ? new Date(b.endAt).getTime() : Infinity;
        return aEnd - bEnd;
      });
  }, [schedules]);
  // Find the currently active schedule
  const findActiveSchedule = useCallback(() => {
    const now = getNow();
    return (
      validSchedules.find((s) => {
        const start = new Date(s.startAt).getTime();
        const end = s.endAt ? new Date(s.endAt).getTime() : Infinity;
        return start <= now && now < end;
      }) || null
    );
  }, [validSchedules, getNow]);
  // For recurring: determine if we should show schedule or default content
  const getRecurringPhase = useCallback(
    (schedule) => {
      if (!schedule.displayDuration || !schedule.gapDuration) {
        return "schedule"; // Non-recurring, always show schedule
      }
      const now = getNow();
      const startMs = new Date(schedule.startAt).getTime();
      const elapsed = now - startMs;
      const cycleLength = (schedule.displayDuration + schedule.gapDuration) * 1000;
      const positionInCycle = elapsed % cycleLength;
      // First part of cycle = show schedule, second part = show default
      if (positionInCycle < schedule.displayDuration * 1000) {
        return "schedule";
      } else {
        return "default";
      }
    },
    [getNow],
  );

  const tick = useCallback(() => {
    const activeSchedule = findActiveSchedule();
    const now = getNow();
    if (!activeSchedule) {
      if (lastActiveIdRef.current !== null) {
        // Only send "Completed" if the schedule ended naturally (reached endAt)
        // NOT if it was cancelled/removed from the schedule list
        const endedNaturally =
          lastScheduleEndTimeRef.current !== null && now >= lastScheduleEndTimeRef.current;
        // P1-7: Guard callback — only call if it is a function
        if (endedNaturally && typeof onScheduleComplete === "function") {
          onScheduleComplete({
            scheduleId: lastActiveIdRef.current,
            completedAt: Date.now(),
          });
        }
        lastActiveIdRef.current = null;
        lastScheduleEndTimeRef.current = null;
      }
      setActiveScheduleUrl(null);
      setDisplayMode("default");
      return;
    }
    // Check if this is a different schedule (new one starting or switched)
    if (lastActiveIdRef.current !== activeSchedule.scheduleId) {
      // Complete the previous schedule if there was one
      if (lastActiveIdRef.current !== null) {
        // Only send "Completed" if the previous schedule ended naturally
        const endedNaturally =
          lastScheduleEndTimeRef.current !== null && now >= lastScheduleEndTimeRef.current;
        // P1-7: Guard callback — only call if it is a function
        if (endedNaturally && typeof onScheduleComplete === "function") {
          onScheduleComplete({
            scheduleId: lastActiveIdRef.current,
            completedAt: Date.now(),
          });
        }
      }
      // Activate the new schedule
      // P1-7: Guard callback — only call if it is a function
      if (typeof onScheduleActivate === "function") {
        onScheduleActivate({
          scheduleId: activeSchedule.scheduleId,
          url: activeSchedule.url,
          startedAt: Date.now(),
        });
      }
      lastActiveIdRef.current = activeSchedule.scheduleId;
      // Track the end time for natural expiration detection
      lastScheduleEndTimeRef.current = activeSchedule.endAt
        ? new Date(activeSchedule.endAt).getTime()
        : null;
    }
    // Determine what to display
    const phase = getRecurringPhase(activeSchedule);
    if (phase === "schedule") {
      setActiveScheduleUrl(activeSchedule.url);
      setDisplayMode("schedule");
    } else {
      setActiveScheduleUrl(null);
      setDisplayMode("default");
    }
  }, [findActiveSchedule, getRecurringPhase, getNow, onScheduleActivate, onScheduleComplete]);

  useEffect(() => {
    tick();
    // P2-2: Adaptive tick — 1s when schedules are active, 30s when idle
    const interval = validSchedules.length > 0 ? 1000 : 30000;
    tickRef.current = setInterval(tick, interval);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [tick, validSchedules.length]);

  return {
    activeScheduleUrl,
    displayMode,
    validScheduleCount: validSchedules.length,
  };
}
